import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useBlockedUserIds } from "@/hooks/hub/useHubBlocks";

/**
 * LR HUB feed read + optimistic posting.
 *
 * READ: `hub_posts` where moderation_status='approved' AND is_deleted=false,
 * newest first, keyset paginated on created_at. RLS additionally lets the
 * author see their OWN posts (including 'flagged' ones, shown as "in review").
 *
 * WRITE: there is NO client insert path — posts go through the
 * `moderate-hub-post` edge function (service-role insert after moderation).
 *
 * ISOLATION: hub_posts only.
 */

export const HUB_PAGE_SIZE = 20;

export type HubModerationStatus = "pending" | "approved" | "flagged" | "rejected";

export interface HubPost {
  id: string;
  author_id: string;
  body: string;
  content_type: string;
  /** Topic feed the post belongs to (defaults to General server-side). */
  feed_id?: string | null;
  audience: string;
  tags: string[];
  like_count: number;
  comment_count: number;
  /** Denormalized per-type reaction counts (hub_post_reactions trigger). */
  reaction_counts?: Record<string, number> | null;
  moderation_status: HubModerationStatus;
  moderation_reason: string | null;
  is_deleted: boolean;
  edited_at?: string | null;
  /** Set by the admin-only hub_admin_pin_post RPC (1 pin per feed). */
  pinned_at?: string | null;
  created_at: string;
  /** Joined feed info (slug + name) used for the feed-label pill. */
  feed?: { slug: string; name: string } | null;

}

/** A post the user just submitted, rendered at the top while it settles. */
export interface HubPendingPost {
  tempId: string;
  body: string;
  content_type: string;
  tags: string[];
  /** Target feed slug/label, carried so the card shows the right feed at once. */
  feed_slug?: string;
  feed_name?: string;
  created_at: string;
  state: "posting" | "error";
  error?: string;
}

const HUB_FEED_KEY = ["hub-feed"] as const;

/** Keyset cursor: created_at plus id, so created_at ties can't drop rows. */
export interface HubFeedCursor {
  created_at: string;
  id: string;
}

async function fetchPage(
  cursor: HubFeedCursor | null,
  userId: string | null,
  feedId: string | null,
  blockedIds: string[],
): Promise<HubPost[]> {
  let q = supabase
    .from("hub_posts")
    .select("*, feed:hub_feeds(slug, name)")
    .eq("is_deleted", false)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(HUB_PAGE_SIZE);

  // Home (null) = all feeds mixed; a feedId narrows to that single feed.
  if (feedId) q = q.eq("feed_id", feedId);

  // Server-side: never fetch posts authored by users the viewer has blocked.
  if (blockedIds.length) q = q.not("author_id", "in", `(${blockedIds.join(",")})`);

  // Approved posts for everyone, plus the caller's own in-review posts.
  if (userId) q = q.or(`moderation_status.eq.approved,author_id.eq.${userId}`);
  else q = q.eq("moderation_status", "approved");

  if (cursor) {
    q = q.or(
      `created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`,
    );
  }

  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as HubPost[]).filter(
    (p) => p.moderation_status !== "rejected" || p.author_id === userId,
  );
}

export function useHubFeed(feedId: string | null = null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  const { blockedIds } = useBlockedUserIds();
  const [pending, setPending] = useState<HubPendingPost[]>([]);
  const queryKey = useMemo(
    () => [...HUB_FEED_KEY, userId ?? "anon", feedId ?? "home", blockedIds.join(",")],
    [userId, feedId, blockedIds],
  );

  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: null as HubFeedCursor | null,
    queryFn: ({ pageParam }) => fetchPage(pageParam, userId, feedId, blockedIds),
    getNextPageParam: (lastPage) => {
      if (lastPage.length < HUB_PAGE_SIZE) return undefined;
      const last = lastPage[lastPage.length - 1];
      return { created_at: last.created_at, id: last.id } satisfies HubFeedCursor;
    },
    staleTime: 30_000,
  });

  const posts = useMemo(() => (query.data?.pages ?? []).flat(), [query.data]);

  /** Adds the optimistic row instantly at the very top of the feed. */
  const addPending = useCallback((p: Omit<HubPendingPost, "state">) => {
    setPending((prev) => [{ ...p, state: "posting" }, ...prev]);
  }, []);

  const removePending = useCallback((tempId: string) => {
    setPending((prev) => prev.filter((p) => p.tempId !== tempId));
  }, []);

  const failPending = useCallback((tempId: string, error: string) => {
    setPending((prev) => prev.map((p) => (p.tempId === tempId ? { ...p, state: "error", error } : p)));
  }, []);

  /** Settles a confirmed post: drop the placeholder, prepend the real row. */
  const settlePending = useCallback(
    (tempId: string, post: HubPost) => {
      setPending((prev) => prev.filter((p) => p.tempId !== tempId));
      qc.setQueryData<{ pages: HubPost[][]; pageParams: unknown[] }>(
        queryKey,
        (old) => {
          if (!old) return old;
          const [first = [], ...rest] = old.pages;
          if (first.some((p) => p.id === post.id)) return old;
          return { ...old, pages: [[post, ...first], ...rest] };
        },
      );
    },
    [qc, queryKey],
  );

  /** Replaces a post in place after a (re-moderated) edit. */
  const patchPost = useCallback(
    (post: HubPost) => {
      qc.setQueryData<{ pages: HubPost[][]; pageParams: unknown[] }>(
        queryKey,
        (old) =>
          old
            ? { ...old, pages: old.pages.map((page) => page.map((p) => (p.id === post.id ? post : p))) }
            : old,
      );
    },
    [qc, queryKey],
  );

  /** Drops a post from the feed after the author soft-deletes it. */
  const removePost = useCallback(
    (postId: string) => {
      qc.setQueryData<{ pages: HubPost[][]; pageParams: unknown[] }>(
        queryKey,
        (old) =>
          old
            ? { ...old, pages: old.pages.map((page) => page.filter((p) => p.id !== postId)) }
            : old,
      );
    },
    [qc, queryKey],
  );

  return {
    posts,
    pending,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: !!query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: query.fetchNextPage,
    addPending,
    removePending,
    failPending,
    settlePending,
    patchPost,
    removePost,
    refetch: query.refetch,
  };

}

export default useHubFeed;
